/**
 * A porta entre a tela da preditiva (módulo 18; 0105) e o servidor. A mesa e o
 * celular usam as mesmas chamadas e os mesmos ganchos; toda gravação declara o
 * seu retorno (R04) onde a mutação é montada.
 */
import { useQuery } from '@tanstack/react-query';

import { comCorpo, pedir } from '@/lib/pedido';

import type {
  BaseMinima,
  Categoria,
  ConferenciaDaPlanilha,
  DetalheDaFalha,
  DetalheDaInvestigacao,
  FiltroDaFalha,
  Investigacao,
  OpcoesDaProposta,
  OrigemDaLeitura,
  RespostaDaRecorrencia,
  RespostaDaSugestao,
  RespostaDasFalhas,
  RespostaDasPrevisoes,
  RespostaDasPropostas,
  RespostaDoCatalogo,
  RespostaDosModelos,
  ResultadoDaClassificacao,
  ResultadoDaRodada,
  ResultadoDoBacktest,
  Resumo,
  Rodada,
  Sinal,
  SituacaoDaPrevisao,
  TipoDeModelo,
} from './preditiva';

export interface DadosDaClassificacao {
  readonly ordemId: string;
  readonly componenteId: string;
  readonly modoId: string | null;
  readonly categoria: Categoria;
  readonly confianca: 'confirmada' | 'provavel';
  readonly leitura: { readonly valor: number; readonly origem: OrigemDaLeitura } | null;
  readonly sugestaoId: string | null;
  readonly observacao: string | null;
}

const id = (x: string) => encodeURIComponent(x);

export const apiDaPreditiva = {
  resumo: () => pedir<Resumo>('/preditiva/resumo'),
  falhas: (situacao: FiltroDaFalha, pagina: number, equipamento?: string) =>
    pedir<RespostaDasFalhas>(`/preditiva/falhas?situacao=${situacao}&pagina=${String(pagina)}${equipamento ? `&equipamento=${id(equipamento)}` : ''}`),
  falha: (ordemId: string) => pedir<DetalheDaFalha>(`/preditiva/falhas/${id(ordemId)}`),
  catalogo: () => pedir<RespostaDoCatalogo>('/preditiva/catalogo'),
  recorrencias: (de?: string, ate?: string) =>
    pedir<RespostaDaRecorrencia>(`/preditiva/recorrencias${de || ate ? `?${[de ? `de=${de}` : '', ate ? `ate=${ate}` : ''].filter(Boolean).join('&')}` : ''}`),
  investigacoes: () => pedir<{ investigacoes: readonly Investigacao[] }>('/preditiva/investigacoes'),
  investigacao: (x: string) => pedir<DetalheDaInvestigacao>(`/preditiva/investigacoes/${id(x)}`),
  modelos: () => pedir<RespostaDosModelos>('/preditiva/modelos'),
  rodadas: (x: string) => pedir<{ rodadas: readonly Rodada[] }>(`/preditiva/modelos/${id(x)}/rodadas`),
  previsoes: (situacao: 'todas' | SituacaoDaPrevisao, pagina: number, modelo?: string) =>
    pedir<RespostaDasPrevisoes>(`/preditiva/previsoes?situacao=${situacao}&pagina=${String(pagina)}${modelo ? `&modelo=${id(modelo)}` : ''}`),
  ordensDaPrevisao: (x: string) =>
    pedir<{ ordens: readonly { id: string; numero: number; falhouEm: string; leituraNaFalha: number | null; etapa: string }[] }>(`/preditiva/previsoes/${id(x)}/ordens`),
  propostas: (situacao: 'todas' | 'proposta' | 'aceita' | 'recusada') => pedir<RespostaDasPropostas>(`/preditiva/propostas?situacao=${situacao}`),
  opcoesDaProposta: () => pedir<OpcoesDaProposta>('/preditiva/propostas/opcoes'),
  maquinas: (termo: string) =>
    pedir<{ maquinas: readonly { id: string; codigo: string; descricao: string; codigoInterno?: string | null; placa?: string | null }[] }>(
      `/preditiva/maquinas?termo=${encodeURIComponent(termo)}`,
    ),

  classificar: (d: DadosDaClassificacao) => comCorpo<ResultadoDaClassificacao>('POST', '/preditiva/falhas', d),
  corrigir: (falhaId: string, d: Partial<Omit<DadosDaClassificacao, 'ordemId' | 'sugestaoId'>>) =>
    comCorpo<ResultadoDaClassificacao>('PATCH', `/preditiva/falhas/${id(falhaId)}`, d),
  anular: (falhaId: string, motivo: string) => comCorpo<ResultadoDaClassificacao>('PATCH', `/preditiva/falhas/${id(falhaId)}`, { anulacao: { motivo } }),
  sugerir: (ordemId: string) => comCorpo<RespostaDaSugestao>('POST', `/preditiva/falhas/${id(ordemId)}/sugestao`),
  cadastrarComponente: (d: { codigo: string; nome: string; sistemaId: string | null; modeloId: string | null; observacao: string | null }) =>
    comCorpo<{ id: string; codigo: string; nome: string }>('POST', '/preditiva/componentes', d),
  editarComponente: (x: string, d: { ativo?: boolean; nome?: string }) => comCorpo<{ id: string; codigo: string; nome: string; ativo: boolean }>('PATCH', `/preditiva/componentes/${id(x)}`, d),
  cadastrarModo: (d: { codigo: string; nome: string; observacao: string | null }) => comCorpo<{ id: string; codigo: string; nome: string }>('POST', '/preditiva/modos', d),
  editarModo: (x: string, d: { ativo?: boolean; nome?: string }) => comCorpo<{ id: string; codigo: string; nome: string; ativo: boolean }>('PATCH', `/preditiva/modos/${id(x)}`, d),
  conferirPlanilha: (d: { nomeArquivo: string; conteudo: string }) => comCorpo<ConferenciaDaPlanilha>('POST', '/preditiva/catalogo/conferencia', d),
  importarPlanilha: (d: { nomeArquivo: string; conteudo: string }) =>
    comCorpo<{ componentes: number; modos: number; deFora: number }>('POST', '/preditiva/catalogo/importacao', d),
  cadastrarLimiar: (d: { modeloId: string; componenteId: string | null; tipo: Sinal; sentido: 'acima' | 'abaixo'; limite: number; documentoId: string; pagina: number; trecho: string | null }) =>
    comCorpo<{ id: string; tipo: Sinal; sentido: 'acima' | 'abaixo'; limite: number; pagina: number }>('POST', '/preditiva/limiares', d),
  editarLimiar: (x: string, ativo: boolean) => comCorpo<{ id: string; ativo: boolean }>('PATCH', `/preditiva/limiares/${id(x)}`, { ativo }),
  abrirInvestigacao: (d: { titulo: string; modeloId: string | null; componenteId: string | null; modoId: string | null; hipotese: string; ordemIds: readonly string[] }) =>
    comCorpo<{ id: string; titulo: string; evidencias: number }>('POST', '/preditiva/investigacoes', d),
  incluirEvidencia: (x: string, ordemId: string, observacao: string | null) =>
    comCorpo<{ id: string; numero: number | null }>('POST', `/preditiva/investigacoes/${id(x)}/evidencias`, { ordemId, observacao }),
  concluirInvestigacao: (x: string, situacao: 'concluida' | 'descartada', conclusao: string) =>
    comCorpo<{ id: string; titulo: string; situacao: string }>('POST', `/preditiva/investigacoes/${id(x)}/conclusao`, { situacao, conclusao }),
  criarModelo: (d: { nome: string; tipo: TipoDeModelo; modeloId: string; componenteId: string; modoId: string | null; parametros: Record<string, number | string> }) =>
    comCorpo<{ id: string; nome: string; versao: number }>('POST', '/preditiva/modelos', d),
  novaVersao: (x: string, parametros?: Record<string, number | string>) =>
    comCorpo<{ id: string; nome: string; versao: number }>('POST', `/preditiva/modelos/${id(x)}/versao`, parametros ? { parametros } : {}),
  backtest: (x: string, corte: string) => comCorpo<ResultadoDoBacktest>('POST', `/preditiva/modelos/${id(x)}/backtest`, { corte }),
  piloto: (x: string) => comCorpo<{ id: string; nome: string; situacao: string; pilotoDesde: string }>('POST', `/preditiva/modelos/${id(x)}/piloto`),
  rodada: (x: string) => comCorpo<ResultadoDaRodada>('POST', `/preditiva/modelos/${id(x)}/rodada`),
  adotar: (x: string) => comCorpo<{ id: string; nome: string; situacao: string }>('POST', `/preditiva/modelos/${id(x)}/adocao`),
  retirar: (x: string, motivo: string) => comCorpo<{ id: string; nome: string; situacao: string }>('POST', `/preditiva/modelos/${id(x)}/retirada`, { motivo }),
  desfecho: (x: string, d: { situacao: 'confirmada' | 'falso_alerta'; ordemId: string | null; nota: string | null }) =>
    comCorpo<{ id: string; situacao: string; equipamento: string; componente: string; numero: number | null; antecedencia: number | null; unidade: 'h' | 'dias' }>(
      'POST',
      `/preditiva/previsoes/${id(x)}/desfecho`,
      d,
    ),
  propor: (d: {
    tipo: 'inspecao' | 'ajuste_de_plano';
    equipamentoId: string | null;
    planoId: string | null;
    tarefaId: string | null;
    componenteId: string | null;
    investigacaoId: string | null;
    previsaoId: string | null;
    intervaloProposto: number | null;
    documentoId: string | null;
    pagina: number | null;
    contrariaFabricante: boolean;
    justificativa: string;
  }) => comCorpo<{ id: string; tipo: string; contrariaFabricante: boolean; intervaloAtual: number | null; intervaloProposto: number | null; gatilho: string | null; equipamento: string | null }>('POST', '/preditiva/propostas', d),
  decidir: (x: string, aceitar: boolean, nota: string) =>
    comCorpo<{ id: string; situacao: string; tipo: string; ordem: { id: string; numero: number; rotulo: string } | null; revisaoPlanoId: string | null }>(
      'POST',
      `/preditiva/propostas/${id(x)}/decisao`,
      { aceitar, nota },
    ),
  mudarBase: (d: Omit<BaseMinima, 'definidaPelaEmpresa'> & { motivo: string }) => comCorpo<BaseMinima>('POST', '/preditiva/base-minima', d),
};

/** As leituras, com a chave do cache que as gravações invalidam (tudo sob `preditiva`). */
export const useResumoDaPreditiva = (ligado = true) => useQuery({ queryKey: ['preditiva', 'resumo'], queryFn: apiDaPreditiva.resumo, enabled: ligado });
export const useFalhas = (situacao: FiltroDaFalha, pagina: number, equipamento?: string) =>
  useQuery({ queryKey: ['preditiva', 'falhas', situacao, pagina, equipamento ?? null], queryFn: () => apiDaPreditiva.falhas(situacao, pagina, equipamento) });
export const useFalha = (ordemId: string | null) =>
  useQuery({ queryKey: ['preditiva', 'falha', ordemId], queryFn: () => apiDaPreditiva.falha(ordemId ?? ''), enabled: ordemId !== null });
export const useCatalogoDeFalhas = (ligado = true) => useQuery({ queryKey: ['preditiva', 'catalogo'], queryFn: apiDaPreditiva.catalogo, enabled: ligado });
export const useRecorrencias = (de?: string, ate?: string) =>
  useQuery({ queryKey: ['preditiva', 'recorrencias', de ?? null, ate ?? null], queryFn: () => apiDaPreditiva.recorrencias(de, ate) });
export const useInvestigacoes = () => useQuery({ queryKey: ['preditiva', 'investigacoes'], queryFn: apiDaPreditiva.investigacoes });
export const useInvestigacao = (x: string | null) =>
  useQuery({ queryKey: ['preditiva', 'investigacao', x], queryFn: () => apiDaPreditiva.investigacao(x ?? ''), enabled: x !== null });
export const useModelos = () => useQuery({ queryKey: ['preditiva', 'modelos'], queryFn: apiDaPreditiva.modelos });
export const useRodadas = (x: string | null) => useQuery({ queryKey: ['preditiva', 'rodadas', x], queryFn: () => apiDaPreditiva.rodadas(x ?? ''), enabled: x !== null });
export const usePrevisoes = (situacao: 'todas' | SituacaoDaPrevisao, pagina: number, modelo?: string) =>
  useQuery({ queryKey: ['preditiva', 'previsoes', situacao, pagina, modelo ?? null], queryFn: () => apiDaPreditiva.previsoes(situacao, pagina, modelo) });
export const useOrdensDaPrevisao = (x: string | null) =>
  useQuery({ queryKey: ['preditiva', 'ordens-da-previsao', x], queryFn: () => apiDaPreditiva.ordensDaPrevisao(x ?? ''), enabled: x !== null });
export const usePropostas = (situacao: 'todas' | 'proposta' | 'aceita' | 'recusada') =>
  useQuery({ queryKey: ['preditiva', 'propostas', situacao], queryFn: () => apiDaPreditiva.propostas(situacao) });
export const useOpcoesDaProposta = (ligado: boolean) => useQuery({ queryKey: ['preditiva', 'opcoes-da-proposta'], queryFn: apiDaPreditiva.opcoesDaProposta, enabled: ligado });
export const useMaquinasDaPreditiva = (termo: string, ligado: boolean) =>
  useQuery({ queryKey: ['preditiva', 'maquinas', termo], queryFn: () => apiDaPreditiva.maquinas(termo), enabled: ligado });
